import { Row } from '@/app/components/Flex/Flex'
import { Spinner } from '@/app/components/Spinner/Spinner'
import ArrowRight from '@/app/icons/arrow-right.svg?react'
import SearchSvg from '@/app/icons/search.svg?react'
import classNames from 'classnames'
import type { ComponentChild, ComponentChildren, FunctionalComponent, GenericEventHandler, HTMLInputTypeAttribute } from 'preact'
import styles from './index.module.scss'

type InputProps = {
  readOnly?: boolean
  autoFocus?: boolean
  disabled?: boolean
  type?: HTMLInputTypeAttribute
  class?: string
  placeholder?: string
  value?: string
  maxLength?: number
  minLength?: number
  onFocus?: () => void
  onBlur?: () => void
  onInput?: GenericEventHandler<HTMLInputElement>
  onChange?: GenericEventHandler<HTMLInputElement>
  onClick?: () => void
}

export const Input:
  & FunctionalComponent<{
    inputmode?: string
    autocomplete?: string
    transparent?: boolean
    radius?: 'sm'
    transparableDisabled?: boolean
    readOnly?: boolean
    autoFocus?: boolean
    value?: string
    type?: HTMLInputTypeAttribute
    disabled?: boolean
    loading?: boolean
    showSpinner?: boolean
    showSearch?: boolean
    showArrow?: boolean
    submit?: boolean
    submitDisabled?: boolean
    placeholder?: string
    maxLength?: number
    minLength?: number
    rightBtn?: ComponentChild
    onClick?: () => void
    onInput?: (value: string) => void
    onChange?: (value: string) => void
    onFocus?: () => void
    onBlur?: () => void
    children?: (props: InputProps) => ComponentChildren
  }>
  & {
    Line: FunctionalComponent
    Block: FunctionalComponent<{
      radius?: 's' | 'm' | 'l'
      label: ComponentChildren
      action?: ComponentChildren
    }>
  } = props => {
    const inputProps: InputProps = {
      readOnly: props.readOnly,
      maxLength: props.maxLength,
      minLength: props.minLength,
      autoFocus: props.autoFocus,
      disabled: props.disabled || props.loading,
      type: props.type ?? 'text',
      class: styles.input,
      placeholder: props.placeholder,
      value: props.value ?? '',
      onInput: e => {
        props.onInput?.(e.currentTarget.value)
      },
      onChange: e => {
        props.onChange?.(e.currentTarget.value)
      },
      onFocus: props.onFocus,
      onBlur: props.onBlur,
    }

    return (
      <div
        onClick={props.onClick}
        class={classNames(styles.root, {
          [styles.submit]: props.submit,
          [styles.disabled]: props.transparableDisabled && props.disabled,
          [styles.rSmall]: props.radius === 'sm',
          [styles.transparent]: props.transparent,
          [styles.readOnly]: props.readOnly,
          [styles.pointer]: props.onClick && !props.disabled,
        })}
      >
        {props.children
          ? props.children(inputProps)
          : (
            <input
              {...inputProps}
              inputmode={props.inputmode}
              autocomplete={props.autocomplete}
            />
          )}

        {props.rightBtn && (
          <div class={styles.rightBtn}>
            {props.rightBtn}
          </div>
        )}

        {props.loading || props.showSpinner
          ? (
            <div class={styles.spinner}>
              <Spinner size={20} />
            </div>
          )
          : props.submit
          ? (
            <button
              type='submit'
              class={styles.submitBtn}
              disabled={props.submitDisabled || props.disabled}
            >
              <div class={styles.submitIcon}>
                <ArrowRight width={24} height={24} />
              </div>
            </button>
          )
          : props.showSearch
          ? (
            <div class={classNames(styles.submitBtn, styles.prevent)}>
              <div class={styles.submitIcon}>
                <SearchSvg width={20} height={20} />
              </div>
            </div>
          )
          : props.showArrow
          ? (
            <div class={classNames(styles.submitBtn, styles.prevent)}>
              <div class={styles.submitIcon}>
                <ArrowRight width={20} height={20} strokeWidth={1.5} />
              </div>
            </div>
          )
          : null}
      </div>
    )
  }

Input.Line = props => (
  <div class={styles.line}>
    {props.children}
  </div>
)

Input.Block = props => (
  <Row gap={10}>
    <label class={classNames(styles.block, styles[`br${props.radius ?? 'm'}`])}>
      <div class={styles.label}>
        {props.label}
      </div>

      {props.children}
    </label>
    {props.action && (
      <div class={styles.action}>
        {props.action}
      </div>
    )}
  </Row>
)
